import dayjs from "dayjs";
import {useEffect, useRef, useState} from "react";
import {isMobile} from "react-device-detect";
import type {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import {Controller} from "react-hook-form";
import SignatureCanvas from "react-signature-canvas";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import {z} from "zod";

export const signaturesSchema = z
  .object({
    signatureTechnicien: z.string().min(1, "Signature du technicien requise"),
    signatureTechnicienDate: z.string(),
    conserveSignatureTechnicien: z.boolean().default(false),
    signatureClient: z.string().optional(),
    signatureClientDate: z.string().optional(),
    clientRefuseSigner: z.boolean().default(false),
  })
  .refine((data) => data.signatureClient || data.clientRefuseSigner, {
    message: "Le client doit signer ou refuser explicitement",
    path: ["signatureClient"],
  });

export type SignaturesFormData = z.infer<typeof signaturesSchema>;
export type TechnicianSignatureSource = "empty" | "applied" | "drawn";

type Props = {
  control: Control<SignaturesFormData>;
  errors: FieldErrors<SignaturesFormData>;
  disabled: boolean;
  watch: UseFormWatch<SignaturesFormData>;
  getValues: UseFormGetValues<SignaturesFormData>;
  setValue: UseFormSetValue<SignaturesFormData>;
  technicianSignatureSource: TechnicianSignatureSource;
  hasStoredSignature: boolean;
  showConserveOption: boolean;
  onTechnicianDraw: () => void;
  onTechnicianClear: () => void;
  onConserveChange: (conserve: boolean) => Promise<void>;
};

export function SignaturesForm({
  control,
  errors,
  disabled,
  watch,
  getValues,
  setValue,
  technicianSignatureSource,
  hasStoredSignature,
  showConserveOption,
  onTechnicianDraw,
  onTechnicianClear,
  onConserveChange,
}: Props) {
  const techSignatureRef = useRef<SignatureCanvas>(null);
  const clientSignatureRef = useRef<SignatureCanvas>(null);
  const clientRefuseSigner = watch("clientRefuseSigner");
  const technicianSignature = watch("signatureTechnicien");
  const [dialog, setDialog] = useState<"closed" | "information" | "confirm" | "error">("closed");
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    const savedTechSig = technicianSignature;
    let active = true;
    if (technicianSignatureSource !== "applied" && savedTechSig && techSignatureRef.current) {
      const img = new Image();
      img.onload = () => {
        if (
          !active ||
          !techSignatureRef.current ||
          getValues("signatureTechnicien") !== savedTechSig
        ) {
          return;
        }
        const {width, height} = techSignatureRef.current.getCanvas();
        techSignatureRef.current.fromDataURL(savedTechSig, {width, height});
      };
      img.src = savedTechSig;
    }
    return () => {
      active = false;
    };
  }, [getValues, technicianSignature, technicianSignatureSource]);

  useEffect(() => {
    const savedClientSig = getValues("signatureClient");
    let active = true;
    if (savedClientSig && clientSignatureRef.current && !clientRefuseSigner) {
      const img = new Image();
      img.onload = () => {
        if (
          !active ||
          !clientSignatureRef.current ||
          getValues("signatureClient") !== savedClientSig
        ) {
          return;
        }
        const {width, height} = clientSignatureRef.current.getCanvas();
        clientSignatureRef.current.fromDataURL(savedClientSig, {width, height});
      };
      img.src = savedClientSig;
    }
    return () => {
      active = false;
    };
  }, [getValues, clientRefuseSigner]);

  const canvasWidth = isMobile ? 280 : 450;
  const canvasHeight = isMobile ? 120 : 200;

  return (
    <>
      <h5 style={{marginBottom: "12px"}}>Signatures</h5>

      <p style={{marginBottom: "4px", fontSize: "14px"}}>
        <strong>Technicien</strong> (obligatoire)
      </p>
      {errors.signatureTechnicien && (
        <p style={{color: "red", fontSize: "12px", margin: 0}}>
          {errors.signatureTechnicien.message}
        </p>
      )}
      <div
        style={{
          border: "1px solid #ccc",
          borderRadius: "4px",
          backgroundColor: "#fff",
          marginBottom: "4px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {technicianSignatureSource === "applied" && technicianSignature ? (
          <img
            src={technicianSignature}
            alt="Signature du technicien conservée"
            width={canvasWidth}
            height={canvasHeight}
            style={{objectFit: "contain"}}
          />
        ) : (
          <SignatureCanvas
            ref={techSignatureRef}
            canvasProps={{
              width: canvasWidth,
              height: canvasHeight,
              className: "signature-canvas",
              style: {width: `${canvasWidth}px`, height: `${canvasHeight}px`},
            }}
            backgroundColor="white"
            onEnd={handleTechSignatureEnd}
          />
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          marginBottom: "12px",
        }}
      >
        <div style={{minWidth: 0}}>
          {showConserveOption && (
            <>
              <Controller
                name="conserveSignatureTechnicien"
                control={control}
                render={({field}) => (
                  <FormGroup switch style={{marginBottom: 0}}>
                    <Input
                      type="switch"
                      role="switch"
                      id="field-conserveSignatureTechnicien"
                      checked={field.value}
                      onChange={(event) =>
                        handleToggleConserve(event.target.checked, field.onChange)
                      }
                      disabled={disabled || withdrawing}
                    />
                    <Label for="field-conserveSignatureTechnicien" check>
                      Conserver
                    </Label>
                  </FormGroup>
                )}
              />
              <small className="text-muted d-block">
                Pour mes prochaines attestations de ramonage
              </small>
              <Button color="link" className="p-0" type="button" onClick={handleOpenInformation}>
                Plus d’information
              </Button>
            </>
          )}
        </div>
        <Button
          color="primary"
          outline
          onClick={handleClearTech}
          disabled={disabled}
          size="sm"
          style={{flexShrink: 0}}
        >
          Recommencer
        </Button>
      </div>

      <p style={{marginBottom: "4px", fontSize: "14px"}}>
        <strong>Client</strong>
      </p>

      <FormGroup check style={{marginBottom: "8px"}}>
        <Controller
          name="clientRefuseSigner"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="checkbox"
              id="field-clientRefuseSigner"
              checked={field.value}
              onChange={(e) => {
                field.onChange(e.target.checked);
                if (e.target.checked) {
                  setValue("signatureClient", undefined);
                  setValue("signatureClientDate", undefined);
                  if (clientSignatureRef.current) {
                    clientSignatureRef.current.clear();
                  }
                }
              }}
              disabled={disabled}
              value=""
            />
          )}
        />
        <Label check for="field-clientRefuseSigner">
          Le client refuse de signer
        </Label>
      </FormGroup>

      {!clientRefuseSigner && (
        <>
          {errors.signatureClient && (
            <p style={{color: "red", fontSize: "12px", margin: 0}}>
              {errors.signatureClient.message}
            </p>
          )}
          <div
            style={{
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: "#fff",
              marginBottom: "4px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <SignatureCanvas
              ref={clientSignatureRef}
              canvasProps={{
                width: canvasWidth,
                height: canvasHeight,
                className: "signature-canvas",
                style: {width: `${canvasWidth}px`, height: `${canvasHeight}px`},
              }}
              backgroundColor="white"
              onEnd={handleClientSignatureEnd}
            />
          </div>
          <div style={{display: "flex", justifyContent: "flex-end"}}>
            <Button color="secondary" onClick={handleClearClient} disabled={disabled} size="sm">
              Recommencer
            </Button>
          </div>
        </>
      )}
      <Modal isOpen={dialog !== "closed"} backdrop="static" keyboard={false} centered>
        {dialog === "information" ? (
          <>
            <ModalHeader>Conserver ma signature</ModalHeader>
            <ModalBody>
              <p>
                Si vous activez « Conserver », votre signature sera disponible pour vos prochaines
                attestations de ramonage.
              </p>
              <p>C’est facultatif : vous pouvez continuer à signer sans la conserver.</p>
              <p>Vous pouvez arrêter de la conserver à tout moment ici ou dans votre Profil.</p>
            </ModalBody>
            <ModalFooter>
              <Button color="primary" type="button" onClick={handleCloseDialog}>
                Compris
              </Button>
            </ModalFooter>
          </>
        ) : withdrawing ? (
          <>
            <ModalHeader>Arrêt en cours</ModalHeader>
            <ModalBody>
              <Spinner size="sm" className="me-2" /> Vérification de votre demande…
            </ModalBody>
          </>
        ) : dialog === "error" ? (
          <>
            <ModalHeader>Impossible de vérifier l’arrêt</ModalHeader>
            <ModalBody>
              Nous n’avons pas pu vérifier l’arrêt de la conservation. Réessayez.
            </ModalBody>
            <ModalFooter>
              <Button color="primary" type="button" onClick={handleConfirmWithdraw}>
                Réessayer
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalHeader>Ne plus conserver votre signature ?</ModalHeader>
            <ModalBody>
              Votre signature ne sera plus réutilisée. Vous devrez signer vos prochains documents.
            </ModalBody>
            <ModalFooter>
              <Button color="primary" outline type="button" onClick={handleCloseDialog}>
                Annuler
              </Button>
              <Button color="primary" type="button" onClick={handleConfirmWithdraw}>
                Confirmer
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </>
  );

  function handleTechSignatureEnd() {
    if (techSignatureRef.current) {
      const isEmpty = techSignatureRef.current.isEmpty();
      const dataUrl = isEmpty ? "" : techSignatureRef.current.toDataURL();
      setValue("signatureTechnicien", dataUrl);
      if (!isEmpty) {
        setValue("signatureTechnicienDate", dayjs().toISOString());
        onTechnicianDraw();
      }
    }
  }

  function handleClearTech() {
    if (techSignatureRef.current) {
      techSignatureRef.current.clear();
    }
    setValue("signatureTechnicien", "");
    setValue("signatureTechnicienDate", "");
    onTechnicianClear();
  }

  function handleToggleConserve(checked: boolean, onChange: (value: boolean) => void) {
    if (!checked && hasStoredSignature) {
      setDialog("confirm");
      return;
    }
    onChange(checked);
  }

  function handleOpenInformation() {
    setDialog("information");
  }

  function handleCloseDialog() {
    setDialog("closed");
  }

  async function handleConfirmWithdraw() {
    setWithdrawing(true);
    try {
      await onConserveChange(false);
      setDialog("closed");
    } catch {
      setDialog("error");
    } finally {
      setWithdrawing(false);
    }
  }

  function handleClientSignatureEnd() {
    if (clientSignatureRef.current) {
      const isEmpty = clientSignatureRef.current.isEmpty();
      const dataUrl = isEmpty ? undefined : clientSignatureRef.current.toDataURL();
      setValue("signatureClient", dataUrl);
      if (!isEmpty) {
        setValue("signatureClientDate", dayjs().toISOString());
      }
    }
  }

  function handleClearClient() {
    if (clientSignatureRef.current) {
      clientSignatureRef.current.clear();
      setValue("signatureClient", undefined);
      setValue("signatureClientDate", undefined);
    }
  }
}

export function getSignaturesDefaultValues(): SignaturesFormData {
  return {
    signatureTechnicien: "",
    signatureTechnicienDate: "",
    conserveSignatureTechnicien: false,
    signatureClient: undefined,
    signatureClientDate: undefined,
    clientRefuseSigner: false,
  };
}
